// src/components/modals/OrderIntelModals.jsx - VERSÃO SUPREMA (THEME AWARE + CATEGORIES)
import React, { useState, useEffect } from 'react';
import { FiX, FiClock, FiUser, FiMessageSquare, FiDatabase, FiBox, FiActivity, FiAlertCircle } from 'react-icons/fi';
import { motion } from 'framer-motion';

// --- BASE DO MODAL ---
const ModalOverlay = ({ children, title, onClose, icon: Icon }) => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1000] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md">
        <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            className="w-full max-w-2xl border rounded-[3rem] overflow-hidden shadow-2xl">
            <header style={{ borderColor: 'var(--border-color)', backgroundColor: 'rgba(255,255,255,0.03)' }}
                className="px-10 py-8 border-b flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <div style={{ backgroundColor: 'color-mix(in srgb, var(--accent-color), transparent 90%)', color: 'var(--accent-color)' }}
                        className="p-3 rounded-2xl"><Icon size={20} /></div>
                    <h2 className="text-xl font-black uppercase italic tracking-tighter">{title}</h2>
                </div>
                <button onClick={onClose} style={{ color: 'var(--text-secondary)' }}
                    className="p-3 hover:bg-black/10 rounded-full transition-all"><FiX size={24} /></button>
            </header>
            <div className="p-10 max-h-[70vh] overflow-y-auto no-scrollbar">{children}</div>
        </motion.div>
    </motion.div>
);

// --- 📜 HISTÓRICO ---
export const OrderHistoryModal = ({ order, onClose }) => (
    <ModalOverlay title="Linha do Tempo / Auditoria" icon={FiClock} onClose={onClose}>
        <div className="space-y-8 relative">
            <div className="absolute left-[19px] top-2 bottom-2 w-[2px]" style={{ backgroundColor: 'var(--border-color)', opacity: 0.2 }} />
            {order.historico?.map((log, i) => (
                <div key={i} className="relative pl-12">
                    <div style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)' }}
                        className="absolute left-0 top-1 w-10 h-10 rounded-full border flex items-center justify-center z-10">
                        <div style={{ backgroundColor: 'var(--accent-color)' }} className="w-3 h-3 rounded-full animate-pulse" />
                    </div>
                    <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'var(--border-color)' }}
                        className="p-6 rounded-3xl border space-y-3">
                        <div className="flex justify-between items-center">
                            <span style={{ color: 'var(--accent-color)' }} className="text-xs font-black uppercase tracking-widest">{log.fase}</span>
                            <span style={{ color: 'var(--text-secondary)' }} className="text-[10px] opacity-60 font-bold">{new Date(log.data).toLocaleString('pt-BR')}</span>
                        </div>
                        <p style={{ color: 'var(--text-primary)' }} className="text-[11px] leading-relaxed opacity-90 italic">"{log.comentario || 'Sem observações.'}"</p>
                    </div>
                </div>
            ))}
        </div>
    </ModalOverlay>
);

// --- 📊 LOGS ---
export const OrderLogModal = ({ order, onClose }) => (
    <ModalOverlay title="Logs Técnicos" icon={FiDatabase} onClose={onClose}>
        <div style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)' }} className="rounded-3xl p-6 border font-mono text-[10px] overflow-x-auto">
            <pre style={{ color: 'var(--accent-color)' }}>{JSON.stringify(order, null, 4)}</pre>
        </div>
    </ModalOverlay>
);

// --- 📦 DETALHES ---
export const OrderDetailsModal = ({ order, onClose }) => (
    <ModalOverlay title="Dossiê da Missão" icon={FiBox} onClose={onClose}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 font-bold uppercase">
            <div className="space-y-4">
                <h4 style={{ color: 'var(--accent-color)' }} className="text-[10px] tracking-[0.3em]">Logística</h4>
                <div style={{ color: 'var(--text-secondary)' }} className="text-xs space-y-1">
                    <p>CEP: {order.logistica?.enderecoInstalacao?.cep}</p>
                    <p>Rua: {order.logistica?.enderecoInstalacao?.rua}, {order.logistica?.enderecoInstalacao?.numero}</p>
                    <p>Cidade: {order.logistica?.enderecoInstalacao?.cidade} - {order.logistica?.enderecoInstalacao?.estado}</p>
                </div>
            </div>
            <div className="space-y-4">
                <h4 style={{ color: 'var(--accent-color)' }} className="text-[10px] tracking-[0.3em]">Itens Ativos</h4>
                {order.itens?.map((item, idx) => (
                    <div key={idx} style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'var(--border-color)' }} className="p-3 rounded-xl border text-[10px]">
                        <p>{item.titulo}</p>
                        <p style={{ color: 'var(--text-secondary)' }}>Qtd: {item.quantidade} | Valor: R$ {item.precoUnitario}</p>
                    </div>
                ))}
            </div>
        </div>
    </ModalOverlay>
);

// --- 🚦 UPDATE STATUS (COM CATEGORIAS) ---
export const StatusUpdateModal = ({ onClose, onConfirm, newStatus, currentStatus, defaultCat, defaultSub }) => {
    const [comentario, setComentario] = useState('');
    const [categoria, setCategoria] = useState(defaultCat || 'LINHAS_MOVEIS');
    const [subCategoria, setSubCategoria] = useState(defaultSub || 'UNITY_DEVE_AGIR');

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-lg">
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
                style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                className="w-full max-w-xl border rounded-[3rem] p-10 shadow-3xl">
                
                <header className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-4">
                        <div style={{ backgroundColor: 'color-mix(in srgb, var(--accent-color), transparent 90%)', color: 'var(--accent-color)' }} className="p-3 rounded-2xl"><FiActivity size={20} /></div>
                        <h2 className="text-xl font-black uppercase italic tracking-tighter">Comando de Fase</h2>
                    </div>
                    <button onClick={onClose} style={{ color: 'var(--text-secondary)' }}><FiX size={20}/></button>
                </header>

                {/* 📂 GRID DE CATEGORIZAÇÃO */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="space-y-2">
                        <label className="text-[7px] font-black uppercase opacity-40 tracking-widest ml-2">Categoria</label>
                        <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
                            className="w-full bg-black/20 border border-white/5 p-3 rounded-xl text-[10px] font-bold outline-none cursor-pointer">
                            <option value="LINHAS_MOVEIS">LINHAS MÓVEIS</option>
                            <option value="TIM_ULTRA_FIBRA">TIM ULTRA FIBRA</option>
                            <option value="SERVICOS_AVANCADOS">SERVIÇOS AVANÇADOS</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[7px] font-black uppercase opacity-40 tracking-widest ml-2">Responsabilidade</label>
                        <select value={subCategoria} onChange={(e) => setSubCategoria(e.target.value)}
                            className="w-full bg-black/20 border border-white/5 p-3 rounded-xl text-[10px] font-bold outline-none cursor-pointer">
                            <option value="CLIENTE_DEVE_AGIR">CLIENTE DEVE AGIR (EXPOSTO)</option>
                            <option value="CONSULTOR_DEVE_AGIR">CONSULTOR DEVE AGIR</option>
                            <option value="UNITY_DEVE_AGIR">UNITY DEVE AGIR (Q1)</option>
                            <option value="TIM_DEVE_AGIR">TIM DEVE AGIR (Q2)</option>
                            <option value="APROVADO">APROVADOS</option>
                            <option value="REPROVADO">REPROVADOS</option>
                            <option value="INSTALACAO">INSTALAÇÃO</option>
                            <option value="LOGISTICA_ENTREGA">LOGÍSTICA</option>
                            <option value="AGUARDANDO_PORTABILIDADE">PORTABILIDADE</option>
                        </select>
                    </div>
                </div>

                {/* 🏹 AVISO DE EXPOSIÇÃO */}
                {subCategoria === 'CLIENTE_DEVE_AGIR' && (
                    <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/20 rounded-2xl flex items-center gap-3">
                        <FiAlertCircle className="text-orange-500 shrink-0" size={16}/>
                        <p className="text-[9px] font-black uppercase text-orange-500 leading-tight">Atenção: O comentário abaixo será visualizado pelo cliente no portal de rastreio.</p>
                    </div>
                )}

                <textarea autoFocus value={comentario} onChange={(e) => setComentario(e.target.value)}
                    placeholder="Descreva o motivo (Ex: RG validado pelo Agente...)"
                    className="w-full h-32 p-6 rounded-[2rem] bg-black/40 border border-white/10 text-white text-sm outline-none focus:border-accent transition-all resize-none placeholder:opacity-20 font-medium mb-8" />

                <div className="grid grid-cols-2 gap-4">
                    <button onClick={onClose} style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                        className="h-14 rounded-2xl border font-black uppercase text-[10px] tracking-widest hover:bg-black/5">Abortar</button>
                    <button onClick={() => onConfirm(comentario, categoria, subCategoria)} disabled={!comentario.trim()}
                        style={{ backgroundColor: 'var(--accent-color)' }}
                        className="h-14 rounded-2xl text-black font-black uppercase text-[10px] tracking-widest active:scale-95 disabled:opacity-20">Confirmar</button>
                </div>
            </motion.div>
        </motion.div>
    );
};